import { useEffect, useState } from 'react';
import {
  fetchDriverSeasonResults,
  fetchConstructorSeasonResults,
  parseStatus,
} from '../../services/jolpicaClient.js';
import { shortCircuitName, countryFlag } from '../../services/resultsService.js';
import Skeleton from '../common/Skeleton.jsx';

/**
 * StandingsHistory — every race of one season for a single driver or
 * constructor, shown inside an expanded standings row.
 *
 * One network call per entrant, made only when the row is opened, and the
 * jolpicaClient cache means re-opening the same row is free. Fetching this
 * eagerly for a 24-row table would be 24 requests against a 4 req/s limit.
 *
 * Constructors list both cars per race, which is the point of opening one:
 * the interesting thing about a team's season is how its two drivers compared
 * race by race.
 */
export default function StandingsHistory({ season, kind, id, accent }) {
  const [state, setState] = useState({ status: 'loading', races: [] });

  useEffect(() => {
    const ctrl = new AbortController();

    (async () => {
      try {
        const races =
          kind === 'constructor'
            ? await fetchConstructorSeasonResults(season, id, { signal: ctrl.signal })
            : await fetchDriverSeasonResults(season, id, { signal: ctrl.signal });
        if (!ctrl.signal.aborted) setState({ status: 'ready', races });
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('[StandingsHistory]', err);
          setState({ status: 'error', races: [] });
        }
      }
    })();

    return () => ctrl.abort();
  }, [season, kind, id]);

  if (state.status === 'loading') {
    return (
      <div className="space-y-1.5 px-3 pb-3" aria-hidden="true">
        <Skeleton className="h-6 w-full" rounded="rounded-lg" />
        <Skeleton className="h-6 w-full" rounded="rounded-lg" />
        <Skeleton className="h-6 w-full" rounded="rounded-lg" />
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <p className="px-3 pb-3 text-2xs text-ink-tertiary">
        Race-by-race results couldn&apos;t be loaded. Try again in a moment.
      </p>
    );
  }

  if (state.races.length === 0) {
    return (
      <p className="px-3 pb-3 text-2xs text-ink-tertiary">
        No race entries recorded for this {kind === 'constructor' ? 'team' : 'driver'} in {season}.
      </p>
    );
  }

  const summary = summarise(state.races);

  return (
    <div className="pb-2">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 pb-2 text-[10px] font-semibold uppercase tracking-widest-plus text-ink-tertiary">
        <span>{summary.entries} {summary.entries === 1 ? 'entry' : 'entries'}</span>
        {summary.wins > 0 ? <span style={{ color: accent }}>{summary.wins} {summary.wins === 1 ? 'win' : 'wins'}</span> : null}
        {summary.podiums > 0 ? <span>{summary.podiums} podium{summary.podiums === 1 ? '' : 's'}</span> : null}
        {summary.dnfs > 0 ? <span className="text-ink-muted">{summary.dnfs} DNF{summary.dnfs === 1 ? '' : 's'}</span> : null}
      </div>

      <div className="space-y-px">
        {state.races.map((race) => (
          <RaceRow key={race.round} race={race} multi={kind === 'constructor'} />
        ))}
      </div>
    </div>
  );
}

function RaceRow({ race, multi }) {
  const results = race.Results ?? [];
  // Team-mates in finishing order, so the better car is always on the left.
  const ordered = [...results].sort((a, b) => finishRank(a) - finishRank(b));
  const points = ordered.reduce((total, r) => total + (parseFloat(r.points) || 0), 0);

  return (
    <div className="flex items-center gap-2 px-3 py-1.5 text-2xs odd:bg-bg-card2/40">
      <span className="w-6 shrink-0 font-mono tabular-nums text-ink-muted">R{race.round}</span>
      <span className="shrink-0">{countryFlag(race.Circuit?.Location?.country)}</span>
      <span className="min-w-0 flex-1 truncate text-ink-secondary">
        {shortCircuitName(race.raceName)}
      </span>

      <div className="flex shrink-0 items-center gap-1.5">
        {ordered.map((result, i) => (
          <Outcome key={`${result.Driver?.driverId ?? i}`} result={result} showDriver={multi} />
        ))}
      </div>

      <span className="w-8 shrink-0 text-right font-mono tabular-nums text-ink-tertiary">
        {points > 0 ? points : '–'}
      </span>
    </div>
  );
}

/**
 * Outcome — one car's result. A finishing position gets a number; anything else
 * gets its status bucket, because "P20" for a car that crashed on lap 1 is a
 * different claim from twentieth place.
 */
function Outcome({ result, showDriver }) {
  const status = parseStatus(result.status);
  const finished = status === 'Finished' || status === 'Lapped';
  const position = parseInt(result.position, 10);
  const label = finished && Number.isFinite(position) ? `P${position}` : status;

  return (
    <span className="inline-flex items-center gap-1">
      {showDriver ? (
        <span className="font-mono text-[10px] text-ink-muted">
          {result.Driver?.code ?? result.Driver?.familyName?.slice(0, 3).toUpperCase() ?? '—'}
        </span>
      ) : null}
      <span
        className={`inline-block min-w-[2.1rem] rounded px-1 py-0.5 text-center font-mono text-[10px] font-bold tabular-nums ${outcomeStyle(
          finished ? position : null
        )}`}
      >
        {label}
      </span>
    </span>
  );
}

function outcomeStyle(position) {
  if (position === 1) return 'bg-yellow-500/15 text-yellow-400 ring-1 ring-yellow-500/50';
  if (position === 2) return 'bg-zinc-400/15 text-zinc-200 ring-1 ring-zinc-400/50';
  if (position === 3) return 'bg-amber-700/20 text-amber-500 ring-1 ring-amber-700/50';
  if (Number.isFinite(position) && position <= 10) return 'bg-bg-card3 text-ink-secondary';
  if (Number.isFinite(position)) return 'bg-bg-card2 text-ink-tertiary';
  return 'bg-f1-red/10 text-f1-red/80'; // retirement, DNS, disqualification
}

/** Sort key: classified finishers by position, everyone else after them. */
function finishRank(result) {
  const status = parseStatus(result.status);
  const position = parseInt(result.position, 10);
  if ((status === 'Finished' || status === 'Lapped') && Number.isFinite(position)) return position;
  return 100 + (Number.isFinite(position) ? position : 99);
}

function summarise(races) {
  let entries = 0;
  let wins = 0;
  let podiums = 0;
  let dnfs = 0;

  for (const race of races) {
    for (const result of race.Results ?? []) {
      entries++;
      const status = parseStatus(result.status);
      const position = parseInt(result.position, 10);
      if (status === 'Finished' || status === 'Lapped') {
        if (position === 1) wins++;
        if (position <= 3) podiums++;
      } else if (status === 'DNF' || status === 'DSQ') {
        dnfs++;
      }
    }
  }

  return { entries, wins, podiums, dnfs };
}
